import { queryMessagePage, markAllMessagesAsRead, markMessageAsRead } from '@/services/ant-design-pro/api';
import type { ModalProps } from '@/share.d';
import { BellOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Button, Modal, message, List } from 'antd';
import { useEffect, useState } from 'react';
import './MessageCenter.less';

interface MessageRecord {
  id: string;
  messageType: number;
  title: string;
  content: string;
  fromUserId: number;
  fromUserName: string;
  createTime: any;
}

type MessageCenterProps = ModalProps & {
  unreadCount?: number;
};

const MessageCenter = ({ open, handleClose, unreadCount = 0 }: MessageCenterProps) => {
  const [data, setData] = useState<MessageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchList = async (params?: {
    current?: number;
    pageSize?: number;
  }) => {
    setLoading(true);
    try {
      const current = params?.current ?? pagination.current;
      const size = params?.pageSize ?? pagination.pageSize;
      const res = await queryMessagePage(current, size);
      const payload = res?.data || res;

      setData(payload?.records || []);
      setPagination({
        current: payload?.current || current,
        pageSize: payload?.size || size,
        total: payload?.total || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchList();
    }
  }, [open]);

  const handleMarkAllRead = async () => {
    try {
      await markAllMessagesAsRead();
      message.success('全部消息已标记为已读');
      fetchList();
    } catch (error) {
      console.error('Failed to mark all messages as read:', error);
      // message.error('标记失败，请重试');
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markMessageAsRead(id);
      message.success('消息已标记为已读');
      // 重新获取列表
      fetchList();
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      // message.error('标记失败，请重试');
    }
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, current: page });
    fetchList({ current: page });
  };

  return (
    <>
      <Modal 
        width={400}
        style={{ top: 80, marginRight: 60 }}
        className="message-center-modal"
        open={open} 
        onCancel={handleClose} 
        footer={false}
        closable={false}
        title={
          <div className="message-header">
            <div>
              未读消息 ({unreadCount})
            </div>
            <Button 
              type="link" 
              onClick={handleMarkAllRead}
            >
              全部标记为已读
            </Button>
          </div>
        }
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <List
            dataSource={data}
            className="message-list"
            renderItem={(item) => (
              <List.Item 
                className="message-item"
                extra={
                  <Button
                    size='small'
                    className="read-button"
                    onClick={() => handleMarkAsRead(item.id)}
                  >
                    已读
                  </Button>
                }
            >
              <List.Item.Meta
                avatar={<VideoCameraOutlined className="message-avatar" />}
                title={
                  <div className="message-content">
                    <div className="message-title">{item.title}</div>
                    <div className="message-body">{item.content}</div>
                    <div className="message-time">{item.createTime || '-'}</div>
                  </div>
                }
              />
            </List.Item>
          )}
          />
        </div>
        {data?.length > 0 && (
          <div className="message-pagination">
            <span className="page-info">
              第 {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)} 页
            </span>
            <div>
              <Button
                type="link"
                disabled={pagination.current === 1} 
                onClick={() => handlePageChange(pagination.current - 1)}
                className="prev-button"
              >
                上一页
              </Button>
              <Button 
                type="link"
                disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                onClick={() => handlePageChange(pagination.current + 1)}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default MessageCenter;
